import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

export default function PaymentCallback() {
  const [sp] = useSearchParams();
  const reference = sp.get("reference") || sp.get("trxref");
  const nav = useNavigate();
  const { refresh } = useCart();
  const [status, setStatus] = useState("verifying"); // verifying | success | failed | error
  const [message, setMessage] = useState("");
  const [order, setOrder] = useState(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (!reference) { setStatus("error"); setMessage("Missing payment reference"); return; }
    if (attempted.current) return;
    attempted.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/paystack/verify/${reference}`);
        if (data.status === "success") {
          setStatus("success");
          setOrder(data.order);
          refresh();
        } else {
          setStatus("failed");
          // Map Paystack's status word to a user-friendly explanation.
          const s = data.status;
          const msg =
            s === "failed" ? "Your card / mobile money was declined by the processor. Nothing was charged in test mode."
            : s === "abandoned" ? "The payment was cancelled before completion. Nothing was charged in test mode."
            : s === "reversed" ? "The payment was reversed. Nothing was charged in test mode."
            : s === "pending" ? "Your payment is still pending. If your mobile money prompt is still active, complete it and reload this page."
            : `Payment ${s || "was not completed"}. Nothing was charged in test mode.`;
          setMessage(msg);
        }
      } catch (e) {
        setStatus("error");
        setMessage(formatError(e));
      }
    })();
  }, [reference, refresh]);

  return (
    <div className="max-w-lg mx-auto px-6 py-20 text-center">
      {status === "verifying" && (
        <>
          <RefreshCw className="w-14 h-14 mx-auto text-[#1E4D3B] animate-spin" />
          <h1 className="text-2xl font-black text-[#1E4D3B] mt-4">Verifying your payment...</h1>
          <p className="text-[#5C6660] mt-2">Reference: <span className="font-mono">{reference || "—"}</span></p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#1B804E]" data-testid="paycallback-success-icon" />
          <h1 className="text-3xl font-extrabold text-[#1E4D3B] mt-4">Payment confirmed</h1>
          <p className="text-[#5C6660] mt-2">Your test-mode Paystack payment was successful.</p>
          {order && (
            <div className="card-farm p-6 mt-6 text-left">
              <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Reference</div>
              <div className="font-mono font-bold text-lg text-[#1F2421]" data-testid="paycallback-reference">{order.payment.reference}</div>
              <div className="h-px bg-[#E5D9C8] my-4" />
              <div className="flex justify-between font-bold text-[#1E4D3B]"><span>Total paid</span><span>GH₵ {order.total.toFixed(2)}</span></div>
            </div>
          )}
          <div className="flex gap-3 mt-6 justify-center">
            <button onClick={() => nav("/orders")} className="btn-primary px-5 py-3 rounded-xl" data-testid="paycallback-view-orders">View my orders</button>
            <button onClick={() => nav("/marketplace")} className="btn-outline-forest px-5 py-3 rounded-xl">Continue shopping</button>
          </div>
        </>
      )}
      {(status === "failed" || status === "error") && (
        <>
          <AlertTriangle className="w-14 h-14 mx-auto text-[#D93838]" data-testid="paycallback-error-icon" />
          <h1 className="text-2xl font-black text-[#1E4D3B] mt-4">Payment not completed</h1>
          <p className="text-[#5C6660] mt-2" data-testid="paycallback-error-msg">{message}</p>
          <div className="flex gap-3 mt-6 justify-center">
            <button onClick={() => nav("/checkout")} className="btn-primary px-5 py-3 rounded-xl">Try again</button>
            <button onClick={() => nav("/cart")} className="btn-outline-forest px-5 py-3 rounded-xl">Back to cart</button>
          </div>
        </>
      )}
    </div>
  );
}
